import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import FounderSocialLinks from "@/components/common/FounderSocialLinks";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";

export const DISCLAIMER_TEXT =
  "This platform is an independent peer-connection service. It is not affiliated with, operated by, approved by, or endorsed by Alcoholics Anonymous, Narcotics Anonymous, Crystal Meth Anonymous, SMART Recovery, or their respective organizations. Sponsors and members are independent users of the platform.";

export default function Footer() {
  return (
    <footer className="bg-white border-t border-[#E3DED4] mt-16">
      {/* The mobile BottomNav is fixed to the bottom of the viewport, so the
          footer needs its own clearance or the last links sit underneath it and
          taps land on the bottom bar instead. */}
      <div className="max-w-7xl mx-auto px-4 pt-12 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-12">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-[#1F6F78] text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="font-display text-lg font-bold text-[#1F2A2E]">Sponsor Me</span>
            </div>
            <p className="mt-3 text-sm text-[#4B5A60] leading-6">
              Peer-to-peer recovery sponsorship. Browsing is always free, and sponsors never pay.
            </p>
            {/* Travis's own personal accounts, provided by him. These are not
                Sponsor Me business accounts and are never labelled as such. */}
            <div className="mt-5" data-testid="footer-founder-social">
              <h4 className="text-sm font-bold text-[#1F2A2E]">
                Follow Travis, Founder of Sponsor Me
              </h4>
              <FounderSocialLinks
                variant="plain"
                testIdPrefix="footer-founder-social"
                className="mt-1 flex-col gap-0"
              />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#1F2A2E] mb-3">Explore</h4>
            <ul className="space-y-1 md:space-y-2 text-sm text-[#4B5A60]">
              <li><Link to="/sponsors" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Browse Sponsors</Link></li>
              <li><Link to="/become-a-sponsor" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Become a Sponsor</Link></li>
              <li><Link to="/pricing" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Pricing</Link></li>
              <li><Link to="/resources" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Recovery Resources</Link></li>
              <li><Link to="/vs/ai-sponsor" data-testid="footer-vs-ai-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Human Sponsors vs AI</Link></li>
              <li><Link to="/about" data-testid="footer-about-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">About</Link></li>
              <li>
                <Link
                  to="/updates"
                  data-testid="footer-updates-link"
                  onClick={() => trackOutcome(HEYCATCH_EVENTS.PRODUCT_UPDATES_CLICKED)}
                  className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation"
                >
                  Product Updates
                </Link>
              </li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#1F2A2E] mb-3">Safety &amp; Legal</h4>
            <ul className="space-y-1 md:space-y-2 text-sm text-[#4B5A60]">
              <li>
                <Link to="/resources" data-testid="footer-emergency-link" className="inline-flex items-center min-h-[44px] md:min-h-0 font-semibold hover:text-[#1F6F78] touch-manipulation">
                  Need Immediate Help?
                </Link>
              </li>
              <li><Link to="/terms" data-testid="footer-terms-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Terms of Service</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">Privacy Policy</Link></li>
              <li><Link to="/about" data-testid="footer-legal-about-link" className="inline-flex items-center min-h-[44px] md:min-h-0 hover:text-[#1F6F78] touch-manipulation">About Sponsor Me</Link></li>
            </ul>
          </div>
          <div className="rounded-2xl bg-[#F7F4EF] border border-[#E3DED4] p-4">
            <h4 className="text-sm font-bold text-[#1F2A2E] mb-2">Independent Recovery Community</h4>
            <p data-testid="footer-disclaimer" className="text-xs text-[#4B5A60] leading-5">
              {DISCLAIMER_TEXT}
            </p>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-[#E3DED4] flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#7A868B]">
          <span>&copy; {new Date().getFullYear()} Sponsor Me. All rights reserved.</span>
          <span>Sponsors and peer members are not therapists, doctors, or crisis counselors.</span>
        </div>
      </div>
    </footer>
  );
}
